import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileShell from './components/MobileShell';
import { albumService } from '../services/api';
import {
  base64ToStickers,
  stickersToBase64,
  countsFromBase64,
  ALBUM_SECTIONS,
  VISIBLE_TOTAL,
  stickerLocalNumber,
} from '../album/albumData';
import { getSectionCountries } from '../album/albumDecorations';

/**
 * Vista mobile del álbum.
 *
 * Flujo de selección (como pidió el usuario):
 * - Tap = seleccionar/deseleccionar (NO toggle inmediato).
 * - Una barra de acciones inferior aparece con la selección activa:
 *   Agregar (suma copia), Quitar (-1) y Limpiar.
 * - Sólo después de tocar una acción se persiste en backend.
 *
 * Cada país es una "página" del scroll vertical, con un selector horizontal
 * sticky arriba. El FAB queda oculto cuando hay selección para no chocar
 * con la barra de acciones.
 */
const COUNTRY_LIST = ALBUM_SECTIONS.flatMap(section => {
  const countries = getSectionCountries(section);
  if (!countries) return [{ kind: 'fwc', section, from: section.from, to: section.to, code: section.label, name: section.subtitle }];
  return countries.map(c => ({ kind: 'country', country: c, from: c.from, to: c.to, code: c.code, name: c.name, color: c.color }));
});

export default function MobileAlbum() {
  const navigate = useNavigate();
  const [base64, setBase64] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    albumService.getMyAlbum().then(res => setBase64(res.data?.data ?? null)).catch(() => {});
  }, []);

  const stickers = useMemo(() => base64ToStickers(base64), [base64]);
  const counts = useMemo(() => base64 ? countsFromBase64(base64) : null, [base64]);
  const ownedTotal = useMemo(() => stickers.filter(s => s.owned).length, [stickers]);
  const pct = Math.round((ownedTotal / VISIBLE_TOTAL) * 100);

  const toggleSelect = (n) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  };

  const persistCounts = async (mutator) => {
    if (saving || !base64) return;
    const newCounts = counts ? new Uint8Array(counts) : new Uint8Array(VISIBLE_TOTAL);
    selected.forEach(n => mutator(newCounts, n - 1));
    const next = stickersToBase64(stickers.map((s, i) => ({
      ...s,
      owned: newCounts[i] > 0,
      reps: newCounts[i],
    })));
    setBase64(next);
    setSelected(new Set());
    try {
      setSaving(true);
      await albumService.saveMyAlbum(next);
    } finally {
      setSaving(false);
    }
  };

  const handleAdd     = () => persistCounts((c, i) => { c[i] = (c[i] || 0) + 1; });
  const handleRemove  = () => persistCounts((c, i) => { c[i] = Math.max(0, (c[i] || 0) - 1); });
  const handleClear   = () => setSelected(new Set());

  const activeCountry = COUNTRY_LIST[activeIdx] || COUNTRY_LIST[0];
  const activeSlice = stickers.filter(s => s.n >= activeCountry.from && s.n <= activeCountry.to);
  const activeOwned = activeSlice.filter(s => s.owned).length;
  const hasSelection = selected.size > 0;

  return (
    <MobileShell
      title="Mi álbum"
      trailing={
        <button
          type="button"
          onClick={() => navigate('/m')}
          className="p-2 -mr-2 rounded-full hover:bg-surface-container"
          aria-label="Cerrar"
        >
          <span className="material-symbols-outlined text-on-surface-variant">close</span>
        </button>
      }
    >
      <section className="px-5 pt-4 pb-2">
        <div className="flex items-baseline justify-between">
          <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Tu álbum</p>
          <p className="text-xs font-bold tabular-nums">
            <span className="text-primary">{ownedTotal}</span>
            <span className="text-on-surface-variant">/{VISIBLE_TOTAL}</span> · {pct}%
          </p>
        </div>
        <div className="mt-2 h-2 bg-surface-container rounded-full overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
        </div>
      </section>

      <section className="sticky top-14 z-30 bg-surface/95 backdrop-blur border-b border-outline-variant">
        <div className="flex gap-1.5 overflow-x-auto hide-scrollbar px-5 py-3 snap-x snap-mandatory">
          {COUNTRY_LIST.map((c, i) => {
            const active = i === activeIdx;
            return (
              <button
                key={i}
                type="button"
                onClick={() => setActiveIdx(i)}
                className={`shrink-0 snap-start px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wide border transition-colors ${
                  active
                    ? 'bg-primary text-on-primary border-primary'
                    : 'bg-surface text-on-surface-variant border-outline-variant'
                }`}
                style={!active && c.color ? { borderColor: `${c.color}55`, color: c.color } : undefined}
              >
                {c.code}
              </button>
            );
          })}
        </div>
      </section>

      <section
        className="px-5 pt-4"
        style={{ paddingBottom: hasSelection ? 'calc(72px + 56px + env(safe-area-inset-bottom, 0))' : 'calc(56px + env(safe-area-inset-bottom, 0) + 24px)' }}
      >
        <div className="flex items-center gap-3 mb-4">
          <span
            className="w-2 h-8 rounded-sm shrink-0"
            style={{ backgroundColor: activeCountry.color || 'var(--md-sys-color-primary, #10B981)' }}
          />
          <div className="min-w-0">
            <p className="text-lg font-black tracking-tight text-on-surface">{activeCountry.name}</p>
            <p className="text-xs text-on-surface-variant">
              <span className="font-bold tabular-nums">{activeOwned}</span> de <span className="tabular-nums">{activeSlice.length}</span> · {Math.round((activeOwned / activeSlice.length) * 100) || 0}%
            </p>
          </div>
        </div>

        <div className="grid grid-cols-5 gap-2">
          {activeSlice.map(s => (
            <StickerTile
              key={s.n}
              sticker={s}
              localNumber={stickerLocalNumber(s.n)}
              isSelected={selected.has(s.n)}
              onTap={() => toggleSelect(s.n)}
              accent={activeCountry.color || '#10B981'}
            />
          ))}
        </div>
      </section>

      {/* ── Bottom action bar (cuando hay selección) ── */}
      {hasSelection && (
        <div
          className="fixed inset-x-0 z-40 bg-surface border-t border-outline-variant px-3 py-2 flex items-stretch gap-2"
          style={{ bottom: 'calc(56px + env(safe-area-inset-bottom, 0))' }}
        >
          <div className="flex flex-col items-start justify-center px-2 shrink-0">
            <span className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant leading-none">Selección</span>
            <span className="text-base font-black tabular-nums leading-tight">{selected.size}</span>
          </div>
          <button
            type="button"
            onClick={handleAdd}
            disabled={saving}
            className="flex-1 min-h-[44px] rounded-xl bg-primary text-on-primary font-bold text-sm flex items-center justify-center gap-1 disabled:opacity-60"
          >
            <span className="material-symbols-outlined text-base">add_circle</span>
            Agregar
          </button>
          <button
            type="button"
            onClick={handleRemove}
            disabled={saving}
            className="min-h-[44px] min-w-[44px] rounded-xl border border-red-300 text-red-600 bg-surface flex items-center justify-center disabled:opacity-60"
            aria-label="Quitar"
          >
            <span className="material-symbols-outlined text-base">remove_circle</span>
          </button>
          <button
            type="button"
            onClick={handleClear}
            className="min-h-[44px] min-w-[44px] rounded-xl border border-outline-variant text-on-surface-variant bg-surface flex items-center justify-center"
            aria-label="Limpiar"
          >
            <span className="material-symbols-outlined text-base">close</span>
          </button>
        </div>
      )}

      {/* ── FAB matches (oculto si hay selección) ── */}
      {!hasSelection && (
        <button
          type="button"
          onClick={() => navigate('/m/matches')}
          className="fixed right-4 z-30 flex items-center gap-2 px-5 py-3 rounded-full bg-primary text-on-primary shadow-lg shadow-primary/30 font-bold text-sm"
          style={{ bottom: 'calc(56px + env(safe-area-inset-bottom, 0) + 12px)' }}
        >
          <span className="material-symbols-outlined">bolt</span>
          Buscar matches
        </button>
      )}
    </MobileShell>
  );
}

function StickerTile({ sticker, localNumber, isSelected, onTap, accent }) {
  const owned = sticker.owned;
  const baseStyle = owned
    ? { backgroundColor: accent, color: '#ffffff', border: 'none' }
    : { backgroundColor: 'var(--md-sys-color-surface-container, #f3f4f6)', color: 'var(--md-sys-color-on-surface-variant, #4b5563)', border: `2px dashed ${accent}55` };
  return (
    <button
      type="button"
      onClick={onTap}
      className="aspect-square rounded-xl flex items-center justify-center text-base font-black transition-transform active:scale-90 relative"
      style={{
        ...baseStyle,
        minHeight: 56,
        outline: isSelected ? '3px solid #2563eb' : 'none',
        outlineOffset: isSelected ? 2 : 0,
      }}
      aria-pressed={isSelected}
      aria-label={`Figurita ${sticker.n}${owned ? ', tenés' : ', falta'}${isSelected ? ', seleccionada' : ''}`}
    >
      {localNumber}
      {isSelected && (
        <span
          className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-blue-600 text-white flex items-center justify-center"
          style={{ fontSize: 10 }}
        >
          ✓
        </span>
      )}
    </button>
  );
}
